/**
 * NovoTRPage — geração do TR a partir do ETP (F6b).
 *
 * Diferente do DFD e do ETP, o TR não tem formulário de insumos: ele é
 * derivado do ETP aprovado (Art. 6º, XXIII). Esta tela confere o
 * pré-requisito, mostra de onde cada alínea vai sair e dispara a geração.
 */

import {
  faArrowLeft,
  faCheckCircle,
  faCircleXmark,
  faExclamationTriangle,
  faFileContract,
  faGavel,
  faSpinner,
  faWandMagicSparkles,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from 'react-router'
import { gerarTR, novoTR } from '../../api/tr'
import { useToast } from '../../components/Toast'

export function NovoTRPage() {
  const { id } = useParams()
  const idNumerico = Number(id)
  const navegar = useNavigate()
  const { notificar } = useToast()

  const { data, isLoading, error } = useQuery({
    queryKey: ['tr-novo', idNumerico],
    queryFn: () => novoTR(idNumerico),
    enabled: Number.isFinite(idNumerico),
    retry: false,
  })

  const gerar = useMutation({
    mutationFn: () => gerarTR(idNumerico),
    onSuccess: () => navegar(`/tasks/tr/${idNumerico}`),
    onError: (e: Error) => notificar('error', e.message),
  })

  if (isLoading) {
    return (
      <p className="mt-12 text-center text-slate-500">
        <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
        Verificando o ETP…
      </p>
    )
  }
  if (error || !data) {
    return (
      <div className="mx-auto mt-12 max-w-xl rounded-2xl bg-white p-8 text-center shadow-lg dark:bg-dark-card">
        <FontAwesomeIcon icon={faExclamationTriangle} className="mb-4 text-5xl text-amber-400" />
        <h1 className="mb-2 text-2xl font-semibold text-slate-700 dark:text-slate-200">
          Não é possível iniciar o TR
        </h1>
        <p className="mb-6 text-slate-500">
          {(error as Error)?.message ?? 'É necessário gerar o ETP antes de criar o TR.'}
        </p>
        <Link to="/processos" className="inline-flex items-center rounded-lg bg-emerald-600 px-6 py-3 font-semibold text-white hover:bg-emerald-700">
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
          Voltar ao processo
        </Link>
      </div>
    )
  }

  const faltantes = data.secoes_etp.filter((s) => !s.preenchida)

  return (
    <div className="mx-auto max-w-4xl">
      <header className="mb-8 text-center">
        <h1 className="mb-2 text-3xl font-bold text-slate-800 dark:text-slate-100">
          <FontAwesomeIcon icon={faFileContract} className="mr-3 text-emerald-600" />
          Gerar Termo de Referência
        </h1>
        <p className="text-slate-600 dark:text-slate-400">{data.objeto}</p>
      </header>

      <section className="mb-6 rounded-xl border border-emerald-200 bg-emerald-50 p-6 dark:border-emerald-800 dark:bg-emerald-900/20">
        <h2 className="mb-2 text-lg font-semibold text-emerald-800 dark:text-emerald-300">
          <FontAwesomeIcon icon={faGavel} className="mr-2" />
          O TR deriva do ETP
        </h2>
        <p className="text-sm text-emerald-700 dark:text-emerald-400">
          O Art. 6º, XXIII não pede um novo formulário: as 10 alíneas
          obrigatórias e as condições contratuais são desenvolvidas a partir do
          Estudo Técnico Preliminar já aprovado, com IA e RAG.
        </p>
      </section>

      <section className="mb-6 rounded-xl border border-slate-200 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-dark-card">
        <h2 className="mb-4 text-lg font-semibold text-slate-800 dark:text-slate-100">
          Base disponível no ETP
        </h2>
        <ul className="grid grid-cols-1 gap-2 md:grid-cols-2">
          {data.secoes_etp.map((secao) => (
            <li key={secao.chave} className="flex items-start gap-2 text-sm">
              <FontAwesomeIcon
                icon={secao.preenchida ? faCheckCircle : faCircleXmark}
                className={`mt-0.5 ${secao.preenchida ? 'text-emerald-500' : 'text-slate-300'}`}
              />
              <span className={secao.preenchida ? 'text-slate-700 dark:text-slate-300' : 'text-slate-400'}>
                {secao.titulo}
              </span>
            </li>
          ))}
        </ul>
        {faltantes.length > 0 && (
          <p className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
            <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
            {faltantes.length} seção(ões) do ETP em branco. O TR será gerado,
            mas as alíneas que dependem delas ficam mais frágeis — considere
            completar o ETP antes.
          </p>
        )}
      </section>

      <section className="mb-6 rounded-xl border border-slate-200 bg-white p-6 shadow-lg dark:border-slate-700 dark:bg-dark-card">
        <h2 className="mb-4 text-lg font-semibold text-slate-800 dark:text-slate-100">
          O que será gerado
        </h2>
        <ol className="grid grid-cols-1 gap-2 md:grid-cols-2">
          {data.secoes_tr.map((secao) => (
            <li key={secao.chave} className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-300">
              <span className="rounded bg-emerald-100 px-1.5 font-mono text-xs text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300">
                {secao.alinea})
              </span>
              {secao.titulo}
            </li>
          ))}
        </ol>
      </section>

      {data.tem_tr_gerado && (
        <p className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm font-medium text-amber-800 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
          <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
          Já existe um TR gerado neste processo. Gerar novamente substitui o
          conteúdo atual.
        </p>
      )}

      <div className="flex flex-col justify-between gap-3 pb-4 md:flex-row">
        <Link to="/processos" className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-200 px-6 py-3 font-semibold text-slate-700 transition-colors hover:bg-slate-300 dark:bg-dark-hover dark:text-slate-200">
          <FontAwesomeIcon icon={faArrowLeft} />
          Voltar
        </Link>
        <button
          type="button"
          onClick={() => gerar.mutate()}
          disabled={gerar.isPending}
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-3 font-semibold text-white shadow-lg transition-all hover:from-emerald-700 hover:to-teal-700 disabled:opacity-60"
        >
          <FontAwesomeIcon icon={gerar.isPending ? faSpinner : faWandMagicSparkles} spin={gerar.isPending} />
          Gerar TR Com LicitIA
        </button>
      </div>
    </div>
  )
}
